/**
 * bomStructureStats.js — pure, framework-free helpers over a BOM tree as built by
 * `workflow2.0/bomTree.js`'s `buildBomPositionTree` (each node carries `children[]`). No React,
 * no API calls: only array/tree math, so it is unit-testable in isolation (story 117, HU-03).
 *
 * @typedef {Object} BomTreeNode
 * @property {string} uuid
 * @property {{code: string, name?: string}} [resource]
 * @property {BomTreeNode[]} children
 */

/**
 * S31 (RN03.2): total number of components a node groups, counting every descendant recursively
 * (not including the node itself).
 * @param {BomTreeNode} node
 * @returns {number}
 */
const countDescendants = (node) => {
  const children = (node && Array.isArray(node.children)) ? node.children : []
  return children.reduce((total, child) => total + 1 + countDescendants(child), 0)
}

/**
 * S31 (RN03.2): number of tree levels below a node (0 when it has no children).
 * @param {BomTreeNode} node
 * @returns {number}
 */
const countLevelsBelow = (node) => {
  const children = (node && Array.isArray(node.children)) ? node.children : []
  if (children.length === 0) return 0
  return 1 + Math.max(...children.map((child) => countLevelsBelow(child)))
}

/**
 * S36 (RN03.6): uuids of a node and all its descendants — used to confirm/apply a recursive delete.
 * @param {BomTreeNode} node
 * @returns {string[]}
 */
const collectSubtreeUuids = (node) => {
  if (!node) return []
  const children = Array.isArray(node.children) ? node.children : []
  return [node.uuid, ...children.flatMap((child) => collectSubtreeUuids(child))]
}

const resourceCodeOf = (node) => (node && node.resource && node.resource.code) || (node && node.resourceCode) || ''

/**
 * S32 (RN03.8): every occurrence of a component code in the tree, each with the chain of ancestor
 * uuids that must be expanded to reveal it. Returns ALL occurrences (never just the first one).
 * @param {BomTreeNode[]} tree
 * @param {string} code
 * @returns {{uuid: string, ancestorUuids: string[]}[]}
 */
const findComponentOccurrences = (tree, code) => {
  const occurrences = []

  const visit = (nodes, ancestorUuids) => {
    (nodes || []).forEach((node) => {
      if (resourceCodeOf(node) === code) {
        occurrences.push({ uuid: node.uuid, ancestorUuids: [...ancestorUuids] })
      }
      visit(node.children, [...ancestorUuids, node.uuid])
    })
  }

  visit(tree, [])
  return occurrences
}

const resourceNameOf = (node) => (node && node.resource && node.resource.name) || (node && node.resourceName) || ''

/**
 * S32 (RN03.8) as a FILTER, widened by H134 S49 (CA25): the components whose code, name OR position
 * contains the search term (case-insensitive), plus the ancestors needed to keep every match inside
 * its branch. With an empty term everything is visible.
 * @param {BomTreeNode[]} tree
 * @param {string} term
 * @returns {{matchedUuids: Set<string>, visibleUuids: Set<string>}} `matchedUuids` = the rows to list
 * in the table; `visibleUuids` = matches + their ancestor chain, the items to feed the tree view
 */
const filterStructureBySearch = (tree, term) => {
  const matchedUuids = new Set()
  const visibleUuids = new Set()
  const normalizedTerm = String(term || '').trim().toLowerCase()

  const visit = (nodes, ancestorUuids) => {
    (nodes || []).forEach((node) => {
      const haystack = (resourceCodeOf(node) + ' ' + resourceNameOf(node) + ' ' + (node.position || '')).toLowerCase()
      if (!normalizedTerm || haystack.includes(normalizedTerm)) {
        matchedUuids.add(node.uuid)
        visibleUuids.add(node.uuid)
        ancestorUuids.forEach((uuid) => visibleUuids.add(uuid))
      }
      visit(node.children, [...ancestorUuids, node.uuid])
    })
  }

  visit(tree, [])
  return { matchedUuids, visibleUuids }
}

export {
  countDescendants,
  countLevelsBelow,
  collectSubtreeUuids,
  findComponentOccurrences,
  filterStructureBySearch
}
